import {useEffect, useState} from 'react';
import { useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';
import {getPPAList, downloadPPA, deletePPA, markPpaNotPresented, approvePpaNotPresented} from '../../helper/Api';
import {useLocation} from 'react-router-dom';
import { toast } from "react-toastify";
import {
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
} from "@mui/material";
const isPendingForApproval = (row) =>
    String(row.is_pending) === '1' || Number(row.is_pending) === 1;

export default function PPAList({ setLoading, approvalView = 'all' }) {
    const { authData } = useSelector((state) => state.authData);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const batch_id = searchParams.get('batch_id');
    // const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [data, setData] = useState([]);
    const [markModalOpen, setMarkModalOpen] = useState(false);
    const [markModalRow, setMarkModalRow] = useState(null);
    const [markRemark, setMarkRemark] = useState('');
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [approveModalRow, setApproveModalRow] = useState(null);

    const handleChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
        setRowsPerPage(currentRowsPerPage);
        // setCurrentPage(1); // Reset to the first page when rows per page changes
    };

    const deletePPAHandler = async (ppa_id) => {
        try {
            setLoading(true);
            const res = await deletePPA(ppa_id);
            if(res.data.status) {
                await fetchPPAs();
                setLoading(false);
                toast.success(res.data.message, {
                    position: toast.POSITION.TOP_CENTER
                });
            }
        }catch(err){
            setLoading(false);
            toast.error("Something went wrong!", {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    }

    const showMarkNotPresented = (row) =>
        String(authData?.user?.user_type || '').toLowerCase() === 'l1' &&
        isPendingForApproval(row) &&
        Number(row.is_deleted) === 0 &&
        row.payment_mode === 'offline';

    const openMarkNotPresentedModal = (row) => {
        setMarkModalRow(row);
        setMarkRemark('');
        setMarkModalOpen(true);
    };

    const closeMarkNotPresentedModal = () => {
        setMarkModalOpen(false);
        setMarkModalRow(null);
        setMarkRemark('');
    };

    const submitMarkNotPresented = async () => {
        const roleOk =
            String(authData?.user?.user_type || '').toLowerCase() === 'l1';
        if (!roleOk) {
            toast.error('Only L1 users can mark a PPA as not presented.', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        if (!markModalRow) return;

        if (markModalRow.payment_mode !== 'offline') {
            toast.error('This action is only available for offline payment mode.', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        if (!isPendingForApproval(markModalRow)) {
            toast.error('This PPA is not pending for approval anymore.', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const trimmed = markRemark.trim();
        if (!trimmed) {
            toast.error('Please enter a remark before submitting.', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            const res = await markPpaNotPresented({
                ppa_id: markModalRow.id,
                remark: trimmed,
            });
            if (res.data.status) {
                await fetchPPAs();
                closeMarkNotPresentedModal();
                toast.success(res.data.message || 'Updated successfully', {
                    position: toast.POSITION.TOP_CENTER,
                });
            } else {
                toast.error(res.data.message || 'Request failed', {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Something went wrong!';
            toast.error(msg, { position: toast.POSITION.TOP_CENTER });
        } finally {
            setLoading(false);
        }
    };

    const openApproveNotPresentedModal = (row) => {
        setApproveModalRow(row);
        setApproveModalOpen(true);
    };

    const closeApproveNotPresentedModal = () => {
        setApproveModalOpen(false);
        setApproveModalRow(null);
    };

    const submitApproveNotPresented = async () => {
        if (!approveModalRow) return;
        try {
            setLoading(true);
            const res = await approvePpaNotPresented(approveModalRow.id);
            if (res.data.status) {
                await fetchPPAs();
                closeApproveNotPresentedModal();
                toast.success(res.data.message || 'Approved successfully', {
                    position: toast.POSITION.TOP_CENTER,
                });
            } else {
                toast.error(res.data.message || 'Request failed', {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Something went wrong!';
            toast.error(msg, { position: toast.POSITION.TOP_CENTER });
        } finally {
            setLoading(false);
        }
    };

    const conditionalRowStyles = [
        {
            when: (row) => row.is_deleted === "1",
            style: {
            backgroundColor: "#dc354530", // light red shade for deleted rows
            color: "black",
            },
        },
        {
            when: (row) => String(row.is_pending) === "0" || Number(row.is_pending) === 0,
            style: {
            backgroundColor: "#d9ecff", // light blue for non-pending rows
            color: "black",
            },
        },
        {
            when: (row) => row.is_onhold === "1", // or simply row.is_onhold
            style: {
            backgroundColor: "lightyellow",
            color: "black",
            },
        },
    ];

    const fetchPPAs = async () => {
        try {
            const resp = await getPPAList({
                batch_id: batch_id,
                from_date: fromDate,
                to_date: toDate,
                pending: approvalView === 'pending',
            });
            if (resp.data.status) {
                setData(resp.data.result);
            }
        } catch (error) {
            setData([]);
        }
    }

    useEffect(() => {
        fetchPPAs();
    }, [approvalView]);

    const tableData = data;

    const downloadPPAFun = async (url, isDownloaded, ppa_id) => {
        try {
            let formData = {
                ppa_id: ppa_id
            }
            const res = await downloadPPA(formData);
            if (res.data.status) {
                const link = document.createElement('a');
                link.href = url;
                link.download = `ppa_${ppa_id}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                if(!isDownloaded) {
                    fetchPPAs();
                }
            }
        }catch(e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Something went wrong!";
            toast.error(msg, {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    }

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                fontSize: "14px"
            },
        },
    };

    const actionCellStyle = {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "6px",
        whiteSpace: "nowrap",
    };

    const actionIconBtnStyle = {
        width: 36,
        height: 32,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
    };

    const showApprove = (row) =>
        approvalView === 'pending' &&
        String(authData?.user?.user_type || '').toLowerCase() === 'l2' &&
        (String(row.is_pending) === '0' || Number(row.is_pending) === 0) &&
        Number(row.is_deleted) === 0;

    const approvePpaHandler = async (row) => {
        openApproveNotPresentedModal(row);
    };

    const renderRowActions = (row) => {
        const showDelete =
            (row.payment_mode === "online" &&
                !row.bank_settlement_id &&
                Number(row.is_deleted) === 0) ||
            (Number(row.is_deleted) === 0 && Number(row.is_onhold) === 1);
        const notDownloaded = row.is_downloaded == 0;

        return (
            <div style={actionCellStyle}>
                <Tooltip title="Download PPa" arrow placement="top">
                    <span style={{ display: "inline-flex" }}>
                        <button
                            type="button"
                            className={notDownloaded ? "btn btn-danger" : "btn btn-success"}
                            style={actionIconBtnStyle}
                            onClick={() =>
                                downloadPPAFun(row.ppa_url, notDownloaded, row.id)
                            }
                        >
                            <i className="icon-download" />
                        </button>
                    </span>
                </Tooltip>
                {showApprove(row) && (
                    <Tooltip title="Approve" arrow placement="top">
                        <span style={{ display: "inline-flex" }}>
                            <button
                                type="button"
                                className="btn btn-outline-success ppa-approve-btn-hover-red"
                                style={actionIconBtnStyle}
                                onClick={() => approvePpaHandler(row)}
                            >
                                <i className="icon-check_circle" />
                            </button>
                        </span>
                    </Tooltip>
                )}
                {showMarkNotPresented(row) && (
                    <Tooltip title="Mark as not presented" arrow placement="top">
                        <span style={{ display: "inline-flex" }}>
                            <button
                                type="button"
                                className="btn btn-outline-warning"
                                style={actionIconBtnStyle}
                                onClick={() => openMarkNotPresentedModal(row)}
                            >
                                <i className="icon-event_busy" />
                            </button>
                        </span>
                    </Tooltip>
                )}
                {showDelete && (
                    <button
                        type="button"
                        className="btn btn-danger"
                        style={actionIconBtnStyle}
                        onClick={() => deletePPAHandler(row.id)}
                    >
                        <i className="icon-delete" />
                    </button>
                )}
            </div>
        );
    };

    const columns = [
        {
            name: 'MOP',
            selector: (row) => row.payment_mode,
            wrap: true,
            grow: 1
        },
        {
            name: 'PPA No.',
            selector: (row) => row.ppa_no + '-' + row.id,
            wrap: true,
            grow: 1
        },
        {
            name: 'No. of Records',
            selector: (row) => row.invoice_count,
            wrap: true,
            grow: 1
        },
        {
            name: 'Amount',
            selector: (row) => row.amount,
            wrap: true,
            grow: 1
        },
        {
            name: 'Account No',
            selector: (row) => row.account_no,
            wrap: true,
            grow: 1
        },
        {
            name: 'Approval Date/Time',
            selector: (row) => row.approval_time,
            wrap: true,
            grow: 1
        },
        {
            name: 'Payment Status',
            selector: (row) => {
                const isOnline = String(row?.payment_mode || "").toLowerCase() === "online";
                const isDeleted =
                    String(row?.is_deleted) === "1" || Number(row?.is_deleted) === 1;
                const isPendingZero =
                    String(row?.is_pending) === "0" || Number(row?.is_pending) === 0;

                if (isOnline || isDeleted || isPendingZero) {
                    return (
                        <span style={{ color: "#6c757d", fontSize: "14px", fontWeight: 600 }}>
                            NA
                        </span>
                    );
                }

                const total =
                    Number(row?.invoice_count) ||
                    Number(row?.total_records) ||
                    Number(row?.total) ||
                    0;
                const success = Number(row?.success) || Number(row?.success_count) || 0;
                const failure = Number(row?.failure) || Number(row?.failure_count) || 0;
                const pending = Math.max(0, total - success - failure);

                const line = (label, value, valueColor) => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "8px",
                            lineHeight: 1.15,
                        }}
                    >
                        <span style={{ color: "#6c757d", fontWeight: 600 }}>{label}</span>
                        <span style={{ color: valueColor, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                            {value}
                        </span>
                    </div>
                );

                const items = [
                    { key: "total", label: "Total", value: total, color: "#1f5fa8", always: true },
                    { key: "success", label: "Success", value: success, color: "#198754" },
                    { key: "failure", label: "Failure", value: failure, color: "#dc3545" },
                    { key: "pending", label: "Pending", value: pending, color: "#b58100" },
                ].filter((i) => i.always || Number(i.value) > 0);

                return (
                    <Tooltip
                        title={
                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                                {items.map((i) => (
                                    <div key={i.key}>
                                        {i.label}: {i.value}
                                    </div>
                                ))}
                            </div>
                        }
                        arrow
                        placement="top"
                    >
                        <div
                            style={{
                                fontSize: "14px",
                                padding: "2px 0",
                                minWidth: "106px",
                            }}
                        >
                            {items.map((i) => (
                                <div key={i.key}>{line(i.label, i.value, i.color)}</div>
                            ))}
                        </div>
                    </Tooltip>
                );
            },
            wrap: false,
            grow: 0,
            width: "150px",
            allowOverflow: true,
            style: { whiteSpace: "normal" },
        },
        {
            name: 'Action',
            selector: (row) => renderRowActions(row),
            wrap: false,
            grow: 0,
            width: "170px",
            allowOverflow: true,
            style: { whiteSpace: "nowrap" },
        }
    ];

    return (
        <div>
            <Dialog
                open={markModalOpen}
                onClose={closeMarkNotPresentedModal}
                maxWidth="sm"
                fullWidth
                aria-labelledby="mark-not-presented-title"
            >
                <DialogTitle id="mark-not-presented-title">
                    Mark as not presented
                </DialogTitle>
                <DialogContent dividers>
                    <p className="mb-3 text-muted small">
                        You are about to mark this PPA as <strong>not presented</strong>. This
                        update will be recorded against your account. If you wish to continue,
                        please add a brief remark below (required).
                    </p>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Remark"
                        placeholder="Enter reason / note for this action"
                        fullWidth
                        multiline
                        minRows={3}
                        value={markRemark}
                        onChange={(e) => setMarkRemark(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeMarkNotPresentedModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={submitMarkNotPresented}
                        variant="contained"
                        color="warning"
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={approveModalOpen}
                onClose={closeApproveNotPresentedModal}
                maxWidth="sm"
                fullWidth
                aria-labelledby="approve-not-presented-title"
            >
                <DialogTitle id="approve-not-presented-title">
                    Confirm approval
                </DialogTitle>
                <DialogContent dividers>
                    <p className="mb-0 text-muted small">
                        Once you approve this PPA as <strong>not presented</strong>, it will be
                        <strong> permanently removed</strong> along with all invoices linked to it.
                        Please confirm you want to proceed.
                    </p>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeApproveNotPresentedModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={submitApproveNotPresented}
                        variant="contained"
                        color="error"
                    >
                        Yes, approve
                    </Button>
                </DialogActions>
            </Dialog>
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={tableData}
                fixedHeader
                fixedHeaderScrollHeight="600px"
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={tableData.length}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                conditionalRowStyles={conditionalRowStyles}
            />
        </div>
    )
}